import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Paper, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  GRID_START_HOUR,
  GRID_END_HOUR,
  groupEventsByDate,
  layoutDayEvents,
  minutesFromGridStart,
  startOfWeek,
  addDays,
  formatTime,
} from './eventUtils';

const HOUR_HEIGHT = 60; // px, also 1px per minute since 60min == 60px
const GRID_HEIGHT = (GRID_END_HOUR - GRID_START_HOUR) * HOUR_HEIGHT;
const WEEKDAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const EventGridView = ({ events }) => {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  const eventsByDay = useMemo(() => groupEventsByDate(events), [events]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const hours = useMemo(
    () => Array.from({ length: GRID_END_HOUR - GRID_START_HOUR }, (_, i) => GRID_START_HOUR + i),
    []
  );

  const weekLabel = `${weekStart.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })} - ${addDays(
    weekStart,
    6
  ).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, -7))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: 220, textAlign: 'center' }}>
          {weekLabel}
        </Typography>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, 7))}>
          <ChevronRightIcon />
        </IconButton>
        <Button size="small" onClick={() => setWeekStart(startOfWeek(new Date()))}>
          Vandaag
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, minmax(140px, 1fr))', minWidth: 900 }}>
          <Box />
          {weekDays.map((day) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <Box
                key={day.toISOString()}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: isToday ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {WEEKDAYS[(day.getDay() + 6) % 7]}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: isToday ? 700 : 500 }}>
                  {day.getDate()}/{day.getMonth() + 1}
                </Typography>
              </Box>
            );
          })}

          <Box sx={{ position: 'relative', height: GRID_HEIGHT }}>
            {hours.map((hour) => (
              <Typography
                key={hour}
                variant="caption"
                color="text.secondary"
                sx={{ position: 'absolute', top: (hour - GRID_START_HOUR) * HOUR_HEIGHT - 7, right: 4 }}
              >
                {hour}:00
              </Typography>
            ))}
          </Box>

          {weekDays.map((day) => {
            const dayEvents = eventsByDay.get(day.toDateString()) || [];
            const placements = layoutDayEvents(dayEvents);

            return (
              <Box
                key={day.toISOString()}
                sx={{
                  position: 'relative',
                  height: GRID_HEIGHT,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${
                    HOUR_HEIGHT - 1
                  }px, rgba(128,128,128,0.2) ${HOUR_HEIGHT - 1}px, rgba(128,128,128,0.2) ${HOUR_HEIGHT}px)`,
                }}
              >
                {placements.map(({ event, laneIndex, laneCount }) => {
                  const top = minutesFromGridStart(new Date(event.start_time));
                  const bottom = minutesFromGridStart(new Date(event.end_time));
                  const height = Math.max(18, bottom - top);
                  const widthPct = 100 / laneCount;

                  return (
                    <Box
                      key={event.id}
                      title={`${event.name || 'Sessie'} ${formatTime(event.start_time)}-${formatTime(event.end_time)}`}
                      sx={{
                        position: 'absolute',
                        top,
                        height,
                        left: `${laneIndex * widthPct}%`,
                        width: `calc(${widthPct}% - 4px)`,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: 0.75,
                        px: 0.75,
                        py: 0.25,
                        overflow: 'hidden',
                        boxShadow: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.2 }}>
                        {event.name || 'Sessie'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {formatTime(event.start_time)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default EventGridView;
